import {
  fromIso8601Time,
  isValidDate,
  toLocaleTextAsTime,
} from "@/shared/lib/datetime";
import { useLoggingEffect } from "@/shared/lib/react-hooks";
import { Autocomplete } from "@mui/material";
import type { TimeFieldProps } from "@mui/x-date-pickers";
import { forwardRef, useState } from "react";
import { BaseTextField } from "../base-text-field";

const timeOptions = (() => {
  // 00:00 から 23:55 までの 5 分刻みの時間を生成
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 5) {
      options.push(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
      );
    }
  }
  return options;
})();

export const BaseTimeField = forwardRef(
  (
    props: Pick<TimeFieldProps<Date>, "value" | "disabled"> & {
      error?: boolean;
      helperText?: string;
      onChange: (value: Date | undefined) => void;
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const [value, setValue] = useState<string | undefined>();
    useLoggingEffect(
      () => {
        setValue(props.value ? toLocaleTextAsTime(props.value) : undefined);
      },
      [props.value],
      "BaseTimeField",
    );
    const { helperText, error, disabled, onChange, ...rest } = props;
    return (
      <Autocomplete
        ref={ref}
        size="small"
        fullWidth
        value={value ?? ""}
        disableClearable
        options={timeOptions}
        // getOptionLabel={(option) => `${option} (${option})`}
        renderInput={(params) => (
          <BaseTextField
            {...params}
            {...rest}
            placeholder="時間を入力"
            onChange={(e) => {
              const value = e.target.value;
              // - もし半角数字か:以外が入力されている
              // - もし入力されている文字列が5文字
              // ならば、入力を無視する
              if (!/^[0-9:]*$/.test(value) || value?.length > 5) {
                setValue(value?.slice(0, -1));
              }
              // もし入力されている文字列が2文字ならば、:を追加する
              else if (value?.length === 2) {
                setValue(`${value}:`);
              } else {
                setValue(value !== "" ? value : undefined);
              }
              const date = value !== "" ? fromIso8601Time(value) : undefined;
              props.onChange(isValidDate(date) ? date : undefined);
            }}
            sx={{
              minWidth: { xs: 0, sm: 180 },
            }}
          />
        )}
        onChange={(_, value) => {
          setValue(value);
          const date = value !== "" ? fromIso8601Time(value) : undefined;
          props.onChange(isValidDate(date) ? date : undefined);
        }}
      />
    );
  },
);
